import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { FaTrash, FaCalendarAlt, FaBuilding, FaFlag, FaUser, FaMapMarkerAlt, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { api } from "../../../contexts/AuthContext";

const ACCENT = "#60A5FA";
const PRIORITY_VALUES = { low: 25, medium: 50, high: 80 };

const levelFromPriority = (priority) => {
  const value = parseInt(priority, 10) || 50;
  if (value >= 80) return "high";
  if (value >= 50) return "medium";
  return "low";
};

const priorityColor = (level) => {
  if (level === "high") return "#dc3545";
  if (level === "medium") return "#fd7e14";
  return "#28a745";
};

const statusLabel = (status) => {
  if (status === "in_progress") return "In progress";
  if (status === "done") return "Done";
  return "To do";
};

const Task = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchTask = useCallback(async () => {
    try {
      setLoading(true);
      const [taskRes, companiesRes] = await Promise.all([
        api.get(`/api/tasks/${id}/`),
        api.get("/api/companies/"),
      ]);

      const data = taskRes.data;
      setTask(data);
      setEditedTask({
        ...data,
        company: data.company?.id || "",
        priority_level: data.priority_level || levelFromPriority(data.priority),
        due_date: data.due_date ? new Date(data.due_date) : null,
        location: data.location || "",
        with_person: data.with_person || "",
        kind: data.kind || "request",
      });
      setCompanies(companiesRes.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching task:", err);
      if (err.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
        navigate("/login");
      } else if (err.response?.status === 404) {
        setError("Task not found");
      } else {
        setError("Failed to fetch task details");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const handleSave = async () => {
    if (!editedTask.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    if (editedTask.kind === "appointment" && !editedTask.due_date) {
      toast.error("Appointments need a date and time");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        kind: editedTask.kind,
        title: editedTask.title.trim(),
        description: editedTask.description?.trim() || "",
        due_date: editedTask.due_date
          ? new Date(editedTask.due_date).toISOString()
          : null,
        status: editedTask.status,
        priority: PRIORITY_VALUES[editedTask.priority_level] || 50,
        company: editedTask.company ? parseInt(editedTask.company, 10) : null,
        location: editedTask.location?.trim() || "",
        with_person: editedTask.with_person?.trim() || "",
      };

      const response = await api.put(`/api/tasks/${id}/`, payload);
      setTask(response.data);
      setEditedTask({
        ...response.data,
        company: response.data.company?.id || "",
        priority_level:
          response.data.priority_level || levelFromPriority(response.data.priority),
        due_date: response.data.due_date ? new Date(response.data.due_date) : null,
      });
      setIsEditing(false);
      toast.success("Saved");
    } catch (err) {
      console.error("Error updating task:", err);
      const msg =
        err.response?.data?.due_date?.[0] ||
        err.response?.data?.detail ||
        "Failed to update";
      toast.error(typeof msg === "string" ? msg : "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this?")) return;
    try {
      await api.delete(`/api/tasks/${id}/`);
      toast.success("Deleted");
      navigate("/tasks");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate("/tasks")}>
          Back to tasks
        </Button>
      </Container>
    );
  }

  if (!task || !editedTask) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Task not found</Alert>
      </Container>
    );
  }

  const level = task.priority_level || levelFromPriority(task.priority);
  const kind = isEditing ? editedTask.kind : task.kind;
  const kindTitle =
    kind === "appointment" ? "Appointment" : kind === "personal" ? "Personal todo" : "Customer request";
  const kindBadgeVariant =
    kind === "appointment" ? "info" : kind === "personal" ? "warning" : "secondary";

  return (
    <Container className="mt-4" style={{ color: "#fff" }}>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <Link to="/tasks" className="text-decoration-none" style={{ color: ACCENT }}>
            <FaArrowLeft className="me-2" />
            Tasks
          </Link>
          <h2 className="mt-2 mb-1" style={{ color: ACCENT }}>
            {isEditing ? editedTask.title || "Untitled" : task.title}
          </h2>
          <div className="d-flex gap-2 align-items-center">
            <Badge bg={kindBadgeVariant}>{kindTitle}</Badge>
            {task.is_overdue && <Badge bg="danger">Overdue</Badge>}
          </div>
        </div>
        <div className="d-flex gap-2">
          {!isEditing ? (
            <Button variant="primary" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setEditedTask({
                    ...task,
                    company: task.company?.id || "",
                    priority_level: task.priority_level || levelFromPriority(task.priority),
                    due_date: task.due_date ? new Date(task.due_date) : null,
                  });
                }}
              >
                Cancel
              </Button>
              <Button variant="success" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
          <Button variant="danger" onClick={handleDelete}>
            <FaTrash className="me-1" /> Delete
          </Button>
        </div>
      </div>

      <Card className="mb-4 bg-dark text-light border-secondary">
        <Card.Body>
          {isEditing ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={editedTask.kind}
                  onChange={(e) => setEditedTask({ ...editedTask, kind: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="personal">Personal todo</option>
                  <option value="request">Customer request</option>
                  <option value="appointment">Appointment</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editedTask.title || ""}
                  onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editedTask.description || ""}
                  onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ display: "block" }}>
                      {editedTask.kind === "appointment" ? "Date & time *" : "Due date"}
                    </Form.Label>
                    <DatePicker
                      selected={editedTask.due_date}
                      onChange={(date) => setEditedTask({ ...editedTask, due_date: date })}
                      showTimeSelect={editedTask.kind === "appointment"}
                      timeIntervals={15}
                      dateFormat={
                        editedTask.kind === "appointment" ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"
                      }
                      className="form-control bg-dark text-light border-secondary"
                      placeholderText="Select date"
                      isClearable={editedTask.kind !== "appointment"}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editedTask.status}
                      onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                      className="bg-dark text-light border-secondary"
                    >
                      <option value="todo">To do</option>
                      <option value="in_progress">In progress</option>
                      <option value="done">Done</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={editedTask.priority_level}
                      onChange={(e) =>
                        setEditedTask({ ...editedTask, priority_level: e.target.value })
                      }
                      className="bg-dark text-light border-secondary"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {editedTask.kind === "appointment" && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>With (person)</Form.Label>
                      <Form.Control
                        value={editedTask.with_person || ""}
                        onChange={(e) =>
                          setEditedTask({ ...editedTask, with_person: e.target.value })
                        }
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        value={editedTask.location || ""}
                        onChange={(e) =>
                          setEditedTask({ ...editedTask, location: e.target.value })
                        }
                        className="bg-dark text-light border-secondary"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {editedTask.kind === "personal" && (
                <Form.Group className="mb-3">
                  <Form.Label>Note / who (optional)</Form.Label>
                  <Form.Control
                    value={editedTask.with_person || ""}
                    onChange={(e) =>
                      setEditedTask({ ...editedTask, with_person: e.target.value })
                    }
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>
              )}

              {editedTask.kind !== "personal" && (
              <Form.Group className="mb-3">
                <Form.Label>
                  {editedTask.kind === "appointment" ? "Company (optional)" : "Company"}
                </Form.Label>
                <Form.Select
                  value={editedTask.company || ""}
                  onChange={(e) => setEditedTask({ ...editedTask, company: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="">None</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              )}
            </Form>
          ) : (
            <Row>
              <Col md={8}>
                {task.description ? (
                  <p className="mb-4" style={{ whiteSpace: "pre-wrap", color: "#d1d5db" }}>
                    {task.description}
                  </p>
                ) : (
                  <p className="text-muted mb-4">No description</p>
                )}

                <div className="d-flex align-items-center mb-3 gap-2">
                  <FaCalendarAlt style={{ color: ACCENT }} />
                  <span>
                    {task.due_date
                      ? new Date(task.due_date).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          ...(task.kind === "appointment"
                            ? { hour: "2-digit", minute: "2-digit" }
                            : {}),
                        })
                      : "No date set"}
                  </span>
                </div>

                {task.kind === "appointment" && task.with_person && (
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <FaUser style={{ color: ACCENT }} />
                    <span>{task.with_person}</span>
                  </div>
                )}

                {task.kind === "personal" && task.with_person && (
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <FaUser style={{ color: "#fbbf24" }} />
                    <span>{task.with_person}</span>
                  </div>
                )}

                {task.kind === "appointment" && task.location && (
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <FaMapMarkerAlt style={{ color: ACCENT }} />
                    <span>{task.location}</span>
                  </div>
                )}

                {task.company && (
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <FaBuilding style={{ color: ACCENT }} />
                    <span>{task.company.name}</span>
                  </div>
                )}
              </Col>
              <Col md={4}>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <FaFlag className="me-2" />
                    <span className="me-2">Priority</span>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: priorityColor(level),
                        color: "#fff",
                        textTransform: "capitalize",
                      }}
                    >
                      {level}
                    </span>
                  </div>
                  <div>
                    <span className="me-2">Status</span>
                    <Badge
                      bg={
                        task.status === "done"
                          ? "success"
                          : task.status === "in_progress"
                            ? "primary"
                            : "secondary"
                      }
                    >
                      {statusLabel(task.status)}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    Created {new Date(task.created_at).toLocaleString("en-GB")}
                  </small>
                  <small className="text-muted">
                    Updated {new Date(task.updated_at).toLocaleString("en-GB")}
                  </small>
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Task;
